import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarClock, BrainCircuit, Globe2, ArrowRight, Users, Loader2, Lock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const HERO_IMG = "https://images.unsplash.com/photo-1741699428220-65f37f3fbbcb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const FEATURES = [
  { id: "syllabus", icon: CalendarClock, title: "AI Syllabus Architect",
    desc: "Transform any AP or SAT target into a dated, escalating sequence of cognitive milestones." },
  { id: "socratic", icon: BrainCircuit, title: "Socratic AI Tutor",
    desc: "Never handed answers—guided through economics and math logic loops, one question at a time." },
  { id: "impact", icon: Globe2, title: "Global Impact Network",
    desc: "Premium tuition cross-subsidizes non-monetary assets for educators across three regions." },
];

export function HomeWaitlist({ onNavigate }) {
  const [email, setEmail] = useState("");
  const [cohort, setCohort] = useState("Fall 2026");
  const [plan, setPlan] = useState("monthly");
  const [loading, setLoading] = useState(false);
  const [enrolled, setEnrolled] = useState({ count: 0, visible: false });

  useEffect(() => {
    const fetchStats = () =>
      api.get("/stats/enrolled").then(({ data }) => setEnrolled(data)).catch(() => {});
    fetchStats();
    const t = setInterval(fetchStats, 15000); // live increment as registrations land
    return () => clearInterval(t);
  }, []);

  const join = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        email, cohort, plan, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-navy paper-grain">
        <div className="absolute inset-0 opacity-20">
          <img src={HERO_IMG} alt="Grand academic library" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/95 to-navy/70" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-3xl">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 mb-6 px-3 py-1 rounded-full border border-persian-blue/40 bg-persian-blue/10">
              <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-persian-blue font-semibold">
                The Organization for Cognitive Capital
              </span>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
              className="font-serif font-bold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Cultivating High-Cognition Scholars & Global Academic Capital
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
              Master AP &amp; SAT examinations through algorithmic syllabus engineering and step-by-step
              Socratic reasoning—while funding grassroots teachers across vulnerable global communities.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-8 flex flex-wrap gap-3">
              <Button onClick={() => onNavigate("syllabus")} data-testid="hero-explore-syllabus"
                className="bg-persian-rose hover:bg-persian-rosehover text-white font-semibold">
                Build my study plan <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <Button onClick={() => onNavigate("impact")} variant="outline" data-testid="hero-explore-impact"
                className="border-white/30 bg-white/5 text-white hover:bg-white/10">
                See our global impact
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        {/* Waitlist + features */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Waitlist card */}
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.5 }} className="lg:col-span-5">
            <div className="bg-navy rounded-2xl p-8 text-white relative overflow-hidden">
              <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-persian-rose/20 blur-2xl" />
              <div className="relative">
                <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-persian-blue font-semibold">
                  Batch 1 · Global Enrollment Gateway
                </span>
                <h2 className="font-serif text-3xl font-semibold mt-3 leading-tight">
                  Help Us Build the World's First Cognitive Cross-Subsidization Engine. Join the Batch 1 Global Enrollment Gateway.
                </h2>

                {(
                  <>
                    <div data-testid="waitlist-pricing" className="mt-5 rounded-xl border border-persian-rose/30 bg-persian-rose/[0.08] p-4">
                      <div className="grid grid-cols-2 gap-2 p-1 rounded-lg bg-navy/40 mb-3">
                        <button type="button" onClick={() => setPlan("monthly")} data-testid="plan-monthly"
                          className={`rounded-md py-1.5 text-sm font-medium transition-all ${plan === "monthly" ? "bg-persian-rose text-white" : "text-slate-300"}`}>
                          Monthly
                        </button>
                        <button type="button" onClick={() => setPlan("annual")} data-testid="plan-annual"
                          className={`rounded-md py-1.5 text-sm font-medium transition-all ${plan === "annual" ? "bg-persian-rose text-white" : "text-slate-300"}`}>
                          Annual · save 17%
                        </button>
                      </div>
                      <p className="text-sm text-slate-200 leading-relaxed">
                        Unlock the full AI Academic Dashboard for just{" "}
                        <span className="text-persian-rose font-semibold">
                          {plan === "annual" ? "$119.90/year" : "$11.99/month"}
                        </span>
                        {plan === "annual" && <span className="text-slate-400"> (2 months free)</span>}. Select your starting cohort below to secure early access.
                      </p>
                    </div>
                    <form onSubmit={join} className="mt-4 space-y-3">
                      <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                        data-testid="waitlist-email-input" placeholder="your.email@school.edu"
                        className="bg-white/10 border-white/20 text-white placeholder:text-slate-400" />
                      <Select value={cohort} onValueChange={setCohort}>
                        <SelectTrigger data-testid="waitlist-cohort-select"
                          className="bg-white/10 border-white/20 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Fall 2026">Fall 2026 Cohort</SelectItem>
                          <SelectItem value="Spring 2027">Spring 2027 Cohort</SelectItem>
                          <SelectItem value="Summer Intensive">Summer Intensive</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button type="submit" disabled={loading} data-testid="waitlist-submit-button"
                        className="w-full bg-persian-rose hover:bg-persian-rosehover text-white font-semibold">
                        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
                        {plan === "annual" ? "Subscribe securely — $119.90/year" : "Subscribe securely — $11.99/month"}
                      </Button>
                      <p className="text-[11px] text-slate-400 text-center">
                        Secure checkout via Stripe. Cancel anytime.
                      </p>
                    </form>
                  </>
                )}

                {enrolled.visible && (
                  <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    className="mt-6 flex items-center gap-2 text-sm text-slate-300" data-testid="enrolled-ticker">
                    <Users className="h-4 w-4 text-persian-blue" />
                    <span className="font-mono text-persian-blue font-semibold" data-testid="enrolled-count">
                      {enrolled.count.toLocaleString()}
                    </span>
                    scholars already enrolled
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Feature cards */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-1 gap-5">
            {FEATURES.map((f, i) => (
              <motion.button key={f.id} onClick={() => onNavigate(f.id)}
                data-testid={`feature-card-${f.id}`}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="text-left group bg-white rounded-xl border border-slate-200/80 shadow-sm hover:shadow-md hover:border-persian-blue/50 transition-all p-6 flex items-start gap-5">
                <div className="h-12 w-12 shrink-0 rounded-lg bg-navy-soft flex items-center justify-center group-hover:bg-navy transition-colors">
                  <f.icon className="h-6 w-6 text-navy group-hover:text-persian-blue transition-colors" />
                </div>
                <div>
                  <h3 className="font-serif text-xl font-medium text-navy flex items-center gap-2">
                    {f.title}
                    <ArrowRight className="h-4 w-4 text-persian-rose opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" />
                  </h3>
                  <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{f.desc}</p>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
